import AnomalyAlertIcon from '@assets/icons/minor/AnomalyAlertIcon';
import { cn } from '@components/shadcn/utils';
import { ImageryButton } from '@components/shared/ImageryButton/ImageryButton';
import { useNotificationsCenter } from '@contexts/NotificationsCenterContext';
import { useUserNotifications } from '@hooks/queries/notifications/useUserNotifications';
import { routePaths } from '@screens/routes';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import { Amplitude } from 'src/amplitude';
import type { NotificationCenter } from '../NotificationCenter.models';
import { BaseNotification } from './BaseNotification';

function FieldsEnrolledAction({
  notification,
}: {
  readonly notification: NotificationCenter.Notification;
}) {
  const { t } = useTranslation();
  const [_, setParams] = useSearchParams();
  const { setPopoverVisible } = useNotificationsCenter();

  const onClick = () => {
    Amplitude.trackNotificationsAnomalyChangeSettings();
    setPopoverVisible(false);
    setParams({ subscriptionsSettings: 'anomaly-detection' });
  };

  return (
    <React.Fragment>
      <p className="text-sm text-gray-700 whitespace-pre-line mt-1">
        {t(`notifications.${notification.service}.${notification.trigger}`, { amount: 5 })}
      </p>
      <div className="flex gap-3 mt-2">
        <ImageryButton type="softPrimary" onClick={onClick}>
          {t('notifications.actions.change_settings')}
        </ImageryButton>
        <ImageryButton type="softPrimary">
          {t('notifications.actions.how_does_it_work')}
        </ImageryButton>
      </div>
    </React.Fragment>
  );
}

function AnomaliesDetectedAction({
  notification,
}: {
  readonly notification: NotificationCenter.Notification;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setPopoverVisible, markAsRead } = useNotificationsCenter();

  const { data } = useUserNotifications();
  const details = data?.[notification.id];
  const organizationId = details?.user_notification.content.organization_id;
  const propertyId = details?.user_notification.content.property_id;
  const workspaceId = details?.user_notification.content.workspace_id;

  const onClick = () => {
    setPopoverVisible(false);
    markAsRead(notification);

    if (organizationId && propertyId) {
      Amplitude.trackNotificationsAnomalyShowMe('farm');
      navigate(`${routePaths.organizations}/${organizationId}/properties/${propertyId}`);
    } else if (organizationId) {
      Amplitude.trackNotificationsAnomalyShowMe('organization');
      navigate(
        `${routePaths.organizations}/workspace/${workspaceId}/organization/${organizationId}`
      );
    } else {
      Amplitude.trackNotificationsAnomalyShowMe('workspace');
      navigate(`${routePaths.organizations}/workspace/${workspaceId}`);
    }
  };

  if (details) {
    return (
      <React.Fragment>
        <p className="text-sm text-gray-700 whitespace-pre-line mt-1">
          {t(`notifications.${notification.service}.${notification.trigger}`, {
            amount: details.user_notification.content.anomalies_count,
            property_name: details.user_notification.content.property_name,
            organization_name: details.user_notification.content.organization_name,
            workspace_name: details.user_notification.content.workspace_name,
          })}
        </p>
        <div className="flex gap-3 mt-2">
          <ImageryButton type="softPrimary" onClick={onClick}>
            {t('notifications.actions.show_me')}
          </ImageryButton>
        </div>
      </React.Fragment>
    );
  }

  return null;
}

const ActionComponents: {
  [key: string]: React.FC<{ readonly notification: NotificationCenter.Notification }>;
} = {
  fields_enrolled: FieldsEnrolledAction,
  single_property: AnomaliesDetectedAction,
  single_organization: AnomaliesDetectedAction,
  multiple_properties_and_organizations: AnomaliesDetectedAction,
} as const;

function AnomalyDetectionNotification({
  notification,
}: {
  readonly notification: NotificationCenter.Notification;
}) {
  const ActionComponent = ActionComponents[notification.trigger];

  const icon = (
    <div
      className={cn('p-1 bg-sy-blue-10 rounded-lg', {
        ['bg-white']: notification.status === 'UNREAD',
      })}
    >
      <AnomalyAlertIcon width={24} height={24} fill="#06F" />
    </div>
  );

  return (
    <BaseNotification notification={notification} icon={icon} ActionComponent={ActionComponent} />
  );
}

export { AnomalyDetectionNotification };
